import { useCallback, useEffect, useState } from 'react';
import { api, type ClientAccount } from '../api';

export default function Accounts() {
  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [model, setModel] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showToken, setShowToken] = useState<ClientAccount | null>(null);

  const refresh = useCallback(async () => setAccounts(await api.accounts()), []);
  useEffect(() => { void refresh(); }, [refresh]);

  const flash = (ok: boolean, text: string) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };

  const create = async () => {
    if (!name.trim()) return;
    try {
      const acc = await api.createAccount(name.trim(), email.trim() || undefined, model.trim() || undefined);
      setShowCreate(false); setName(''); setEmail(''); setModel('');
      await refresh();
      setShowToken(acc);
    } catch (e) { flash(false, String((e as Error)?.message ?? e)); }
  };

  const toggle = async (a: ClientAccount) => {
    try { await api.patchAccount(a.id, { enabled: !a.enabled }); await refresh(); }
    catch (e) { flash(false, String((e as Error)?.message ?? e)); }
  };

  const resetToken = async (a: ClientAccount) => {
    try { const r = await api.resetToken(a.id); await refresh(); setShowToken({ ...a, token: r.token }); flash(true, '已重置 ' + a.name + ' 的 token'); }
    catch (e) { flash(false, String((e as Error)?.message ?? e)); }
  };

  const del = async (a: ClientAccount) => {
    if (!confirm('确定删除账户「' + a.name + '」？该操作不可恢复。')) return;
    try { await api.deleteAccount(a.id); await refresh(); }
    catch (e) { flash(false, String((e as Error)?.message ?? e)); }
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#272b33]">👥 账户管理</h2>
          <p className="text-xs text-[#898b8f]">为你的用户开设账户，每个账户一个独立 token，可单独启停 / 指定模型</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="rounded-md bg-[#1966ff] px-3 py-1.5 text-sm text-white hover:bg-[#3377ff]">
          ＋ 开设账户
        </button>
      </div>

      {msg && (
        <div className={'mb-3 rounded-md border px-3 py-2 text-xs ' + (msg.ok ? 'border-[#c9f5d7] bg-[#f0fcf4] text-[#0bb359]' : 'border-[#ffdedb] bg-[#fff1f0] text-[#fa2331]')}>
          {msg.text}
        </div>
      )}

      {showCreate && (
        <div className="mb-4 rounded-lg border border-[#edeff2] bg-white p-4">
          <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="账户名称 *"
              className="rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] placeholder-[#bcbdc0] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="邮箱（可选）"
              className="rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] placeholder-[#bcbdc0] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="指定模型（可选，默认用全局）"
              className="rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] placeholder-[#bcbdc0] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
          </div>
          <button onClick={() => void create()} className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500">创建并生成 token</button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[#edeff2] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#edeff2] text-left text-xs text-[#898b8f]">
              <th className="px-3 py-2">名称</th><th className="px-3 py-2">Token</th><th className="px-3 py-2">模型</th>
              <th className="px-3 py-2">状态</th><th className="px-3 py-2">最近使用</th><th className="px-3 py-2 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[#bcbdc0]">还没有账户，点击「开设账户」创建</td></tr>
            )}
            {accounts.map((a) => (
              <tr key={a.id} className="border-b border-[#edeff2]/60 last:border-0">
                <td className="px-3 py-2 text-[#272b33]">{a.name}{a.email && <div className="text-[11px] text-[#bcbdc0]">{a.email}</div>}</td>
                <td className="px-3 py-2">
                  <code className="selectable rounded bg-[#f8fafc] px-1.5 py-0.5 font-mono text-[11px] text-[#0bb359]">{a.token}</code>
                </td>
                <td className="px-3 py-2 text-[#565a60]">{a.model || '默认'}</td>
                <td className="px-3 py-2">
                  <button onClick={() => void toggle(a)}
                    className={'rounded-full px-2 py-0.5 text-[11px] ' + (a.enabled ? 'bg-[#f0fcf4] text-[#0bb359]' : 'bg-[#e1e3e5]/60 text-[#565a60]')}>
                    {a.enabled ? '启用中' : '已停用'}
                  </button>
                </td>
                <td className="px-3 py-2 text-xs text-[#898b8f]">{a.lastUsedAt ? new Date(a.lastUsedAt).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setShowToken(a)} className="ml-2 text-xs text-[#1966ff] hover:text-[#1966ff]">复制 token</button>
                  <button onClick={() => void resetToken(a)} className="ml-2 text-xs text-[#ff9e1f] hover:text-amber-300">重置</button>
                  <button onClick={() => void del(a)} className="ml-2 text-xs text-[#fa2331] hover:text-[#fa2331]">删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showToken && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25" onClick={() => setShowToken(null)}>
          <div className="w-[480px] rounded-xl border border-[#e1e3e5] bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 text-base font-semibold text-[#272b33]">账户「{showToken.name}」的 API Token</h3>
            <code className="selectable block break-all rounded bg-[#f8fafc] p-3 font-mono text-sm text-[#0bb359]">{showToken.token}</code>
            <p className="mt-2 text-xs text-[#898b8f]">客户端 baseUrl 填 <code className="text-[#1966ff]">https://xlb.tangdou2027.top/api/v1</code>，API Key 填此 token。</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => navigator.clipboard.writeText(showToken.token).then(() => { setShowToken(null); flash(true, '已复制到剪贴板'); })}
                className="rounded-md bg-[#1966ff] px-3 py-1.5 text-sm text-white hover:bg-[#3377ff]">复制</button>
              <button onClick={() => setShowToken(null)} className="rounded-md bg-[#f0f2f7] px-3 py-1.5 text-sm text-[#565a60] hover:bg-[#e1e3e5]">关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}